import { useEffect, useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { API, authHeaders } from "@/lib/adminApi";
import PageHeader from "@/components/admin/PageHeader";
import StripeConnectSection from "@/components/admin/StripeConnectSection";

const AvgRepatriationCostCard = () => {
  const { association, refreshAssociation } = useOutletContext();
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (association?.avg_repatriation_cost != null) {
      setInput(String(association.avg_repatriation_cost));
    }
  }, [association]);

  const handleSave = async () => {
    const amount = parseFloat(input);
    if (!amount || amount <= 0) { toast.error("Le montant doit être positif."); return; }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/associations/me/avg-repatriation-cost`,
        { avg_repatriation_cost: amount },
        { headers: authHeaders() }
      );
      toast.success("Coût moyen de rapatriement mis à jour.");
      refreshAssociation();
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
        Coût moyen de rapatriement
      </p>
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <input
            type="number" min="1" step="0.01"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="ex. 3500"
            className="w-full pl-4 pr-10 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">€</span>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !input}
          className="px-4 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.97] disabled:opacity-50"
          style={{ backgroundColor: "#059669" }}
        >
          {saving ? "…" : "Enregistrer"}
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-400">
        Utilisé pour estimer la capacité de couverture du fonds et la prime pure par membre
        (indicateurs de risque du tableau de bord).
      </p>
    </div>
  );
};

const DepositAmountCard = () => {
  const { association, refreshAssociation } = useOutletContext();
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (association?.deposit_amount != null) {
      setInput(String(association.deposit_amount));
    }
  }, [association]);

  const handleSave = async () => {
    const amount = parseFloat(input);
    if (!amount || amount <= 0) { toast.error("Le montant doit être positif."); return; }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/associations/me/deposit-amount`,
        { deposit_amount: amount },
        { headers: authHeaders() }
      );
      toast.success("Acompte d'adhésion mis à jour.");
      refreshAssociation();
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
        Acompte à l'adhésion
      </p>
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <input
            type="number" min="1" step="0.01"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="ex. 50"
            className="w-full pl-4 pr-10 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">€</span>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !input}
          className="px-4 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.97] disabled:opacity-50"
          style={{ backgroundColor: "#059669" }}
        >
          {saving ? "…" : "Enregistrer"}
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-400">
        Montant payé par carte à l'ouverture du lien de paiement quand l'adhérent choisit
        l'échéancier ; le solde est ensuite prélevé en 12 mensualités SEPA. Par défaut&nbsp;: 50&nbsp;€.
      </p>
    </div>
  );
};

export const ParametresPage = () => (
  <section className="fade-up fade-up-1 space-y-8">
    <div>
      <PageHeader
        title="Paramètres"
        subtitle="Cotisation, paiements et compte Stripe de l'association"
        rule
      />
      <div className="space-y-6">
        <StripeConnectSection />
        <DepositAmountCard />
        <AvgRepatriationCostCard />
      </div>
    </div>
  </section>
);

export default ParametresPage;
